import * as React from 'react';
import * as RechartsPrimitive from 'recharts';

import { cn } from 'src/component/ds/utils';

/* shadcn/ui's chart, ported to Tailwind v3 + the `ds-` prefix. Marks are composed by
 * the caller; chartAxis.ts carries our tick and unit-scaling helpers. */

const THEMES = { light: '', dark: '.ds-dark' } as const;

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode,
    icon?: React.ComponentType,
  } & (
    | { color?: string, theme?: never }
    | { color?: never, theme: Record<keyof typeof THEMES, string> }
  )
>;

interface ChartContextProps {
  config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart () {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

type ChartContainerProps = React.ComponentProps<'div'> & {
  config: ChartConfig,
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'],
};

/* Upstream sizes itself with `aspect-video`; here the caller sets a height. */
function ChartContainer ({ id, className, children, config, ...props }: ChartContainerProps) {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot='chart'
        data-chart={chartId}
        className={cn(
          'ds-flex ds-w-full ds-justify-center ds-text-xs',
          '[&_.recharts-cartesian-axis-tick_text]:ds-fill-muted-foreground',
          "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:ds-stroke-chart-grid",
          '[&_.recharts-curve.recharts-tooltip-cursor]:ds-stroke-border',
          "[&_.recharts-dot[stroke='#fff']]:ds-stroke-transparent",
          '[&_.recharts-layer]:ds-outline-none',
          "[&_.recharts-polar-grid_[stroke='#ccc']]:ds-stroke-border",
          '[&_.recharts-radial-bar-background-sector]:ds-fill-muted',
          '[&_.recharts-rectangle.recharts-tooltip-cursor]:ds-fill-muted',
          "[&_.recharts-reference-line_[stroke='#ccc']]:ds-stroke-border",
          '[&_.recharts-sector]:ds-outline-none',
          "[&_.recharts-sector[stroke='#fff']]:ds-stroke-transparent",
          '[&_.recharts-surface]:ds-outline-none',
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

function ChartStyle ({ id, config }: { id: string, config: ChartConfig }) {
  const colorConfig = Object.entries(config).filter(([, item]) => item.theme ?? item.color);

  if (!colorConfig.length) return null;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
      .map(([key, itemConfig]) => {
        const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ?? itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
      })
      .join('\n')}
}
`)
          .join('\n'),
      }}
    />
  );
}

const ChartTooltip = RechartsPrimitive.Tooltip;

type ChartTooltipContentProps = React.ComponentProps<typeof RechartsPrimitive.Tooltip>
  & React.ComponentProps<'div'>
  & {
    hideLabel?: boolean,
    hideIndicator?: boolean,
    indicator?: 'line' | 'dot' | 'dashed',
    nameKey?: string,
    labelKey?: string,
  };

function ChartTooltipContent ({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
}: ChartTooltipContentProps) {
  const { config } = useChart();

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) return null;

    const [item] = payload;
    const key = `${labelKey ?? item?.dataKey ?? item?.name ?? 'value'}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value = !labelKey && typeof label === 'string'
      ? (config[label]?.label ?? label)
      : itemConfig?.label;

    if (labelFormatter) {
      return (
        <div className={cn('ds-font-medium', labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      );
    }

    if (!value) return null;

    return <div className={cn('ds-font-medium', labelClassName)}>{value}</div>;
  }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

  if (!active || !payload?.length) return null;

  const nestLabel = payload.length === 1 && indicator !== 'dot';

  return (
    <div
      className={cn(
        'ds-grid ds-min-w-[8rem] ds-items-start ds-gap-1.5 ds-rounded ds-border ds-border-border',
        'ds-bg-popover ds-px-2.5 ds-py-1.5 ds-text-xs ds-shadow-sm',
        className
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className='ds-grid ds-gap-1.5'>
        {payload
          .filter((item: any) => item.type !== 'none')
          .map((item: any, index: number) => {
            const key = `${nameKey ?? item.name ?? item.dataKey ?? 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color ?? item.payload?.fill ?? item.color;

            return (
              <div
                key={index}
                className={cn(
                  'ds-flex ds-w-full ds-flex-wrap ds-items-stretch ds-gap-2',
                  '[&>svg]:ds-h-2.5 [&>svg]:ds-w-2.5 [&>svg]:ds-text-muted-foreground',
                  indicator === 'dot' && 'ds-items-center'
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            'ds-shrink-0 ds-rounded-[2px]',
                            'ds-border-[var(--color-border)] ds-bg-[var(--color-bg)]',
                            {
                              'ds-h-2.5 ds-w-2.5': indicator === 'dot',
                              'ds-w-1': indicator === 'line',
                              'ds-w-0 ds-border-[1.5px] ds-border-dashed ds-bg-transparent':
                                indicator === 'dashed',
                              'ds-my-0.5': nestLabel && indicator === 'dashed',
                            }
                          )}
                          style={{
                            '--color-bg': indicatorColor,
                            '--color-border': indicatorColor,
                          } as React.CSSProperties}
                        />
                      )
                    )}
                    <div
                      className={cn(
                        'ds-flex ds-flex-1 ds-justify-between ds-leading-none',
                        nestLabel ? 'ds-items-end' : 'ds-items-center'
                      )}
                    >
                      <div className='ds-grid ds-gap-1.5'>
                        {nestLabel ? tooltipLabel : null}
                        <span className='ds-text-muted-foreground'>
                          {itemConfig?.label ?? item.name}
                        </span>
                      </div>
                      {item.value != null && (
                        <span className='ds-font-medium ds-text-popover-foreground ds-tabular-nums'>
                          {typeof item.value === 'number'
                            ? item.value.toLocaleString()
                            : String(item.value)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

const ChartLegend = RechartsPrimitive.Legend;

type ChartLegendContentProps = React.ComponentProps<'div'>
  & { hideIcon?: boolean, nameKey?: string }
  & Pick<RechartsPrimitive.LegendProps, 'payload' | 'verticalAlign'>;

function ChartLegendContent ({
  className,
  hideIcon = false,
  payload,
  verticalAlign = 'bottom',
  nameKey,
}: ChartLegendContentProps) {
  const { config } = useChart();

  if (!payload?.length) return null;

  return (
    <div
      className={cn(
        'ds-flex ds-items-center ds-justify-center ds-gap-4',
        verticalAlign === 'top' ? 'ds-pb-3' : 'ds-pt-3',
        className
      )}
    >
      {payload
        .filter((item: any) => item.type !== 'none')
        .map((item: any, index: number) => {
          const key = `${nameKey ?? item.dataKey ?? 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div
              key={index}
              className={cn(
                'ds-flex ds-items-center ds-gap-1.5',
                '[&>svg]:ds-h-3 [&>svg]:ds-w-3 [&>svg]:ds-text-muted-foreground'
              )}
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className='ds-h-2 ds-w-2 ds-shrink-0 ds-rounded-[2px]'
                  style={{ backgroundColor: item.color }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
    </div>
  );
}

function getPayloadConfigFromPayload (config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== 'object' || payload === null) return undefined;

  const payloadPayload = 'payload' in payload
    && typeof payload.payload === 'object'
    && payload.payload !== null
    ? payload.payload
    : undefined;

  let configLabelKey: string = key;

  if (key in payload && typeof payload[key as keyof typeof payload] === 'string') {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload
    && key in payloadPayload
    && typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  useChart,
};
